import React from 'react';
import Header from '../components/marketing/Header';
import Footer from '../components/marketing/Footer';
import ChatWithAlfred from '../components/ChatWithAlfred';
import CarrierCheck from '../components/CarrierCheck';
import Seo, { faqSchema, breadcrumbSchema } from '../components/Seo';
import {
  useMarketingPage,
  Hero,
  Section,
  SectionHeading,
  ProblemTable,
  CardGrid,
  DarkStatement,
  Steps,
  FAQ,
  FinalCTA
} from '../components/marketing/sections';

/**
 * /drivers — the DRIVER-facing page. The mirror of the homepage.
 *
 * The homepage sells carriers on calling their applicants. This page is for the
 * driver on the other end of that call, and it is free to them. Keeping the two
 * apart matters: a driver who lands on the carrier pitch bounces immediately.
 *
 * ── The one thing to understand before editing this page ──────────────────
 *
 * The verification story here is deliberately about the CARRIER, not the driver.
 *
 * A driver's own record — CDL status, MVR, crash history, Drug & Alcohol
 * Clearinghouse — cannot be looked up from a public web page, and we must never
 * imply otherwise:
 *   • Clearinghouse queries require an FMCSA-registered employer or C-TPA AND the
 *     driver's specific electronic consent granted inside their own Clearinghouse
 *     account, tied to a real hiring action (49 CFR Part 382 Subpart G).
 *   • CDL status lives with state DMVs / CDLIS and is gated by the DPPA.
 *   • PSP crash and inspection history needs the driver's written consent and is
 *     an FCRA consumer report.
 * Producing a report ABOUT a driver that an employer uses to make a hiring
 * decision would make us a consumer reporting agency. We are not one.
 *
 * What IS public, free and instant is the carrier's own FMCSA record — safety
 * rating, operating authority, insurance filings and prior revocations. So
 * Alfred checks the EMPLOYER for the driver. That is legal, it is genuinely
 * useful, and it is the more honest version of the promise.
 *
 * Anything a driver tells Alfred about their own record is SELF-REPORTED, and the
 * copy says so. The real checks happen later, with the carrier, with consent.
 */

// The DRIVER line — +1 832 737 0002, repositioned as the inbound recruiting number
// (Alpha's brief, 2026-07-28). Deliberately NOT the 346 demo line: this page is for a
// driver ringing about work, and 346 answers as the sales demo.
const DRIVER_PHONE_DISPLAY = '(832) 737-0002';
const DRIVER_PHONE_TEL = 'tel:8327370002';

const HERO_STATS = [
  { value: 'Free', label: 'Always free for drivers' },
  { value: '24/7', label: 'Nights, weekends, from the road' },
  { value: 'FMCSA', label: 'Public safety record on every carrier' },
  { value: '0', label: 'Recruiters calling you all week' }
];

const DRIVER_PAIN = [
  { problem: 'You apply to ten carriers and hear back from two', solution: 'Alfred does the calling and follows up for you' },
  { problem: 'Recruiters pitch you a job, not the company behind it', solution: 'Alfred pulls the carrier’s public FMCSA safety record first' },
  { problem: 'The pay on the ad is not the pay on the settlement', solution: 'Alfred asks the pay and home-time questions up front' },
  { problem: 'You find out about the equipment after you have quit your seat', solution: 'Alfred asks the questions people forget in an interview' },
  { problem: 'You can only make calls when you are shut down for the night', solution: 'Alfred works nights and weekends, on your schedule' }
];

const WHAT_ALFRED_DOES = [
  { title: 'Learns what you actually want', description: 'Lanes, home time, pay, equipment, pets, riders, whether you want to be out two weeks or home Friday.' },
  { title: 'Checks the carrier first', description: 'Safety rating, operating authority, insurance on file and prior revocations — from FMCSA’s public record.' },
  { title: 'Does the phone work', description: 'You do not sit on hold. Alfred makes the calls and comes back with real answers.' },
  { title: 'Tells you what it finds — good or bad', description: 'Including when a carrier’s record is worth a second look before you sign anything.' },
  { title: 'Says it’s an AI', description: 'Every call, every time. No cloned voices, no pretending to be a person.' },
  { title: 'Never sells your information', description: 'You are not a lead we resell. Your details go to the carriers you agree to.' }
];

// Only what the two public datasets actually contain. Crash history and
// out-of-service rates live in FMCSA's SMS/CSA data, which is NOT in either of
// them — listing those here would promise something the page cannot show, which
// on a page about trusting a company is the one thing we cannot do.
const CARRIER_CHECKS = [
  'Safety rating — satisfactory, conditional, unsatisfactory, or never rated',
  'Whether the DOT registration is active, or inactive and still advertising',
  'Operating authority — and whether it is carrier authority or only broker authority',
  'Liability insurance on file, against the minimum the carrier is required to carry',
  'Any prior revoked registration tied to the same carrier',
  'Real fleet size and driver count, so “large carrier” can be checked against the filing'
];

const STEPS = [
  { title: 'Tell Alfred what you drive and what you want', description: `Call ${DRIVER_PHONE_DISPLAY} or start the chat on this page. It takes a few minutes and you can do it from the truck.` },
  { title: 'Alfred checks the carriers', description: 'Before anybody pitches you, Alfred pulls each carrier’s public FMCSA record and tells you what it shows.' },
  { title: 'You get the shortlist', description: 'Only the seats that match your lanes, your home time, and your pay — with the safety record attached.' },
  { title: 'You decide who gets your application', description: 'Nothing goes anywhere until you say so. Alfred hands you to a recruiter when you want to talk.' }
];

const FAQ_ITEMS = [
  {
    q: 'Does this cost me anything?',
    a: 'No. Alfred is free for drivers, and always will be. Carriers pay us to reach drivers faster — you are not the product, and we do not sell your information.'
  },
  {
    q: 'Can Alfred check my CDL, my MVR, or my Clearinghouse record?',
    a: 'No, and be careful with anyone who says they can from a website. Your Clearinghouse record can only be queried by an FMCSA-registered employer, with your specific consent given inside your own Clearinghouse account. Your MVR and PSP history need your written authorization too. Those checks happen with the carrier that is actually hiring you, after you consent. What Alfred checks is the carrier — their record is public.'
  },
  {
    q: 'So what does Alfred actually verify?',
    a: 'The company. Alfred pulls the carrier’s public FMCSA record: safety rating, operating authority, insurance filings, and any prior revoked registration. Crash and out-of-service data lives in FMCSA’s SMS system — we link you straight to the official snapshot for that. Either way it is the part most drivers never see until after they have taken the job.'
  },
  {
    q: 'What do you do with what I tell you?',
    a: 'It is used to match you to seats and it goes to the carriers you agree to — nobody else. What you tell Alfred about your own record is self-reported; we do not verify it and we do not present it to anyone as verified.'
  },
  {
    q: 'Am I talking to a real person?',
    a: 'No. Alfred is an AI and says so on every call. If you would rather talk to a human recruiter, say so and Alfred will get you to one.'
  },
  {
    q: 'Will I get calls and texts from a hundred recruiters now?',
    a: 'No. Alfred does not text, and your number does not get handed around. You choose which carriers get your application.'
  },
  {
    q: 'What if I have something on my record?',
    a: 'Tell Alfred. Carriers differ enormously in what they will work with, and the fastest way to waste a month is applying to the ones that will not. Alfred would rather point you at the seats that are actually open to you.'
  }
];

export default function DriverLandingPage() {
  useMarketingPage();

  return (
    <div className="min-h-screen bg-paper text-ink">
      <Seo
        title="CDL Jobs: Check the Carrier First | Alfred Voice"
        description="Free for CDL drivers. Tell Alfred what you want in a seat and it does the calling — and pulls each carrier's public FMCSA safety rating, operating authority and insurance filings before you ever sign on."
        path="/drivers"
        structuredData={[
          faqSchema(FAQ_ITEMS.map((f) => ({ question: f.q, answer: f.a }))),
          breadcrumbSchema([
            { name: 'Alfred Voice', path: '/' },
            { name: 'For Drivers', path: '/drivers' }
          ])
        ]}
      />
      <Header />

      <Hero
        phoneDisplay={DRIVER_PHONE_DISPLAY}
        phoneTel={DRIVER_PHONE_TEL}
        eyebrow="For CDL Drivers · Free"
        title="Know who you’re driving for before you sign."
        secondaryHref="#carrier-check"
        secondaryLabel="Check a carrier now"
        footnote="Free for drivers · Alfred says it’s AI on every call · we never sell your information"
        stats={HERO_STATS}
      >
        Tell Alfred what you want in a seat and it does the phone work. Before any carrier
        pitches you, Alfred pulls their public FMCSA record — safety rating, operating
        authority, insurance on file — and tells you what it found.
      </Hero>

      <ProblemTable
        eyebrow="Sound familiar?"
        title="Finding a seat shouldn’t be this much work."
        rows={DRIVER_PAIN}
        tone="warm"
      />

      {/* The carrier check — the reason this page exists. Kept high. */}
      <Section id="carrier-check" tone="paper">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading
            eyebrow="Check a carrier"
            title="Look up a carrier before you apply."
            subtitle="Type a company name or DOT number. Safety rating, authority and insurance, straight from FMCSA’s public file — with a link to the full official snapshot."
          />
          <CarrierCheck />
        </div>
      </Section>

      <Section tone="warm">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeading
            eyebrow="What Alfred does for you"
            title="A concierge, not a recruiter."
            subtitle="Alfred works for the driver on this side of the call."
          />
          <CardGrid items={WHAT_ALFRED_DOES} tone="warm" />
        </div>
      </Section>

      {/* The single dark moment: the honesty beat about what we cannot check. */}
      <DarkStatement
        eyebrow="Straight about this"
        title="We check the company. We don’t check you."
        items={CARRIER_CHECKS}
        closer="Your record is yours. Nobody pulls it without your consent."
        tone="paper"
      >
        <p>
          No website can look up your Clearinghouse record, your MVR, or your CDL status — those
          need your written consent and a carrier that is actually hiring you. Anyone claiming
          otherwise is either wrong or worth avoiding. What we pull is the carrier’s public FMCSA
          file, and here is what’s in it.
        </p>
      </DarkStatement>

      <Steps
        eyebrow="How it works"
        title="Four steps, and you can start from the truck."
        steps={STEPS}
        tone="warm"
      />

      {/* Discovery. NOTE: ChatWithAlfred renders as a FIXED bottom-right widget,
          not inline — so it is mounted once at the end of the page like every
          other page does it, and this section points at it rather than trying to
          contain it. */}
      <Section id="talk-to-alfred" tone="paper">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionHeading
            eyebrow="Start here"
            title="Tell Alfred what you’re looking for."
            subtitle={`Call ${DRIVER_PHONE_DISPLAY} and talk it through, or open the chat in the corner of this page. Either way it takes a few minutes, and you can do it from the truck.`}
          />
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={DRIVER_PHONE_TEL} className="av-btn-brass px-8 py-4 text-sm">
              Call Alfred — {DRIVER_PHONE_DISPLAY}
            </a>
            <button type="button" data-chat-open className="av-btn-ghost px-8 py-4 text-sm">
              Chat with Alfred
            </button>
          </div>
        </div>
      </Section>

      <FAQ
        eyebrow="Straight answers"
        title="Questions drivers actually ask"
        items={FAQ_ITEMS}
        tone="warm"
      />

      {/* source='driver' so these conversations are distinguishable from carrier
          leads. ⚠️ The backend persona (AlfredVoice-v2 core, routes/alfredChat.js)
          does NOT yet branch on source — see the note to Charlie. Until it does,
          the reply after turn one is the CARRIER pitch. */}
      <ChatWithAlfred
        source="driver"
        callNumber={DRIVER_PHONE_DISPLAY}
        greeting="Hi — I'm Alfred, an AI. I help drivers find a better seat: you tell me what you want in a job, and I check the company's FMCSA safety record before anyone pitches you. What are you driving now?"
      />

      <FinalCTA
        phoneDisplay={DRIVER_PHONE_DISPLAY}
        phoneTel={DRIVER_PHONE_TEL}
        secondaryTo="/drivers#talk-to-alfred"
        secondaryLabel="Tell Alfred what you drive"
        eyebrow="For drivers"
        heading="Let Alfred make the calls."
        body="Free, around the clock, and it checks the carrier before you do."
      />

      <Footer />
    </div>
  );
}
